import { TICK_COMPLETE_S, TICK_WINDOW_S, roundPhase, type Round, type TickPoint } from './model';

/** Live cadence of the static short-market feed files. */
export const BASE_DELAY_MS = 5_000;
/** Slow cadence: rounds feed with no open round, candles of a round that starts later. */
export const IDLE_DELAY_MS = 30_000;
/** Upper bound of the error backoff. */
export const MAX_DELAY_MS = 60_000;
/** Candles of an upcoming round go back to the live cadence this close to its start. */
export const PRE_START_WINDOW_S = 60;
/** Candles of a round that ended this long ago and still 404 are never coming: stop asking. */
export const GIVE_UP_AFTER_END_S = 600;

export type PollState = { failures: number };
export const INITIAL_POLL_STATE: PollState = { failures: 0 };

/**
 * `cadence` overrides the base delay after a success; `final` ends the poll after this fetch.
 * A failure may carry `maxDelay` (do not back off past a deadline) and `final` (give up).
 */
export type ErrorPolicy = { maxDelay?: number; final?: boolean };
export type PollOutcome = { ok: true; cadence?: number; final?: boolean } | ({ ok: false } & ErrorPolicy);

/** 5s, 10s, 20s, 40s, then 60s for every further consecutive failure (404s included). */
export const backoffDelay = (failures: number) => Math.min(BASE_DELAY_MS * 2 ** Math.max(0, failures - 1), MAX_DELAY_MS);

/**
 * Next wait after a fetch, or `null` to stop polling. A success resets the backoff; a failure's
 * backoff is cut at its `maxDelay` (never below the base cadence, like the success-path caps).
 */
export const nextDelay = (state: PollState, outcome: PollOutcome): { state: PollState; delay: number | null } => {
  if (outcome.ok) return { state: INITIAL_POLL_STATE, delay: outcome.final ? null : (outcome.cadence ?? BASE_DELAY_MS) };
  const next = { failures: state.failures + 1 };
  if (outcome.final) return { state: next, delay: null };
  const cap = outcome.maxDelay === undefined ? Infinity : Math.max(BASE_DELAY_MS, outcome.maxDelay);
  return { state: next, delay: Math.min(backoffDelay(next.failures), cap) };
};

// Slow cadence, but never sleep past `deadline` (seconds) and never poll faster than the base.
const slowUntil = (deadline: number, now: number) =>
  Math.max(BASE_DELAY_MS, Math.min(IDLE_DELAY_MS, (deadline - now) * 1000));

/**
 * Rounds feed cadence: 5s while a round is open, otherwise 30s — but never sleep past the
 * start of the next scheduled round.
 */
const nextStart = (rounds: Round[], now: number) =>
  rounds
    .filter(r => r.status !== 'cancelled' && r.status !== 'resolved' && r.startAt > now)
    .reduce((min, r) => Math.min(min, r.startAt), Infinity);

export const roundsCadence = (rounds: Round[], now: number) =>
  rounds.some(r => roundPhase(r, now) === 'open') ? BASE_DELAY_MS : slowUntil(nextStart(rounds, now), now);

/** Rounds feed errors back off as usual, but never sleep past the next round start. */
export const roundsErrorPolicy = (rounds: Round[], now: number): ErrorPolicy => {
  const start = nextStart(rounds, now);
  return { maxDelay: start === Infinity ? undefined : (start - now) * 1000 };
};

/**
 * Candles cadence: an upcoming round more than 60s before its start only carries pre-round
 * context, so 30s; within 60s of the start, while open and until it resolves, 5s.
 */
export const candlesCadence = (round: Round | undefined, now: number) =>
  round && roundPhase(round, now) === 'upcoming' ? slowUntil(round.startAt - PRE_START_WINDOW_S, now) : BASE_DELAY_MS;

const httpStatus = (error: unknown) =>
  typeof error === 'object' && error !== null ? (error as { status?: unknown }).status : undefined;

/**
 * Candles errors: never sleep past T-60s of an upcoming round; give up on a cancelled round whose
 * file is missing (404/410) and on a file still missing (404) over 10 minutes after endAt.
 * Other errors on a cancelled round (5xx, offline) back off until a fetch succeeds, which is final.
 * A 404 before that only backs off: the engine may not have written the file yet.
 */
export const candlesErrorPolicy = (round: Round | undefined, now: number, error: unknown): ErrorPolicy => {
  if (!round) return {};
  const status = httpStatus(error);
  if (round.status === 'cancelled' && (status === 404 || status === 410)) return { final: true };
  if (now > round.endAt + GIVE_UP_AFTER_END_S && status === 404) return { final: true };
  const wake = round.startAt - PRE_START_WINDOW_S;
  return roundPhase(round, now) === 'upcoming' && wake > now ? { maxDelay: (wake - now) * 1000 } : {};
};

/** A round's 1s history file is written once the round resolved with a final price (a cancelled round gets none). */
export const historyWanted = (round: Round | undefined) => round?.status === 'resolved' && round.finalPrice !== undefined;

/** A missing file answer (404/410). */
export const fileNotFound = (error: unknown) => [404, 410].includes(httpStatus(error) as number);

/**
 * History errors: a file that does not parse is final (it is never rewritten). A missing file is final over 10 minutes
 * after endAt on the second missing answer in a row (`missedBefore`), so one retry after the backoff catches a file
 * written seconds after a late resolution; before that it backs off (the writer may not have written it yet). Other
 * errors back off as usual.
 */
export const historyErrorPolicy = (
  round: Round | undefined,
  now: number,
  error: unknown,
  missedBefore = false
): ErrorPolicy => {
  const missing = fileNotFound(error) && missedBefore && !!round && now > round.endAt + GIVE_UP_AFTER_END_S;
  const unreadable = error instanceof SyntaxError || (error instanceof Error && error.name === 'InvalidTickFeed');
  return missing || unreadable ? { final: true } : {};
};

/** A resolved or cancelled round's candles no longer change: one fetch is enough. */
export const isFinalRound = (round?: Round) => round?.status === 'resolved' || round?.status === 'cancelled';

/** Cadence of the 1s tick feed while the price chart wants it (errors back off as usual). */
export const TICKS_DELAY_MS = 2_000;
/** Cadence while the feed still answers but is stale (older than its staleAfterSeconds). */
export const TICKS_STALE_DELAY_MS = 10_000;
/** An ended round whose tick line is still incomplete this long after endAt keeps its candles. */
export const TICKS_AFTER_END_S = 60;

/**
 * The tick feed is read while it can still draw the round: from endAt-900 (the chart window) while
 * upcoming or open, and after endAt until the line is complete (`last` >= endAt-5) or 60s have passed.
 * On a `resume` (the tab returns to a drawn line), after endAt while ticks.json still holds endAt-5.
 * Never for a cancelled round. Otherwise no request at all.
 */
export const ticksPollWanted = (round: Round | undefined, now: number, last?: number, resume = false) => {
  if (!round || round.status === 'cancelled' || now < round.endAt - TICK_WINDOW_S) return false;
  const phase = roundPhase(round, now);
  if (phase === 'open' || phase === 'upcoming') return true;
  const until = resume ? TICK_WINDOW_S - TICK_COMPLETE_S : TICKS_AFTER_END_S;
  return now < round.endAt + until && !(last !== undefined && last >= round.endAt - TICK_COMPLETE_S);
};

/**
 * 2s while the page can draw the feed: fresh, or stale by at most `grace` (the page's hysteresis while tick
 * mode is drawn). 10s once it answers 200 but is too old to draw.
 */
export const ticksCadence = (feed: { updatedAt: number; staleAfterSeconds: number } | undefined, now: number, grace = 0) =>
  feed && now - feed.updatedAt > feed.staleAfterSeconds + grace ? TICKS_STALE_DELAY_MS : TICKS_DELAY_MS;

export type TickFile = 'full' | 'tail';
/** Per poll: `full` forces the next read to ticks.json, `noTail` after a tail 404, `last` = last second read. */
export type TickFetchState = { full: boolean; noTail: boolean; last?: number };
export const TICK_FETCH_START: TickFetchState = { full: true, noTail: false };

/**
 * ticks.json (900s) on poll start, after a gap, a failure, a hidden tab, or a tail that adds no second or is
 * stale; ticks-tail.json (30s) otherwise.
 */
export const nextTickFile = (state: TickFetchState): TickFile => (state.full || state.noTail ? 'full' : 'tail');

/**
 * A tail whose first second is later than the last second read + 1 left a gap: the next read is the full file.
 * So does a tail that adds no new second or is `stale` (a cache can hold the tail behind a fresh full file),
 * and a tail that lands after the tab was hidden (resumeTicks set `full` while it was in flight).
 */
export const afterTickFetch = (
  state: TickFetchState,
  file: TickFile,
  points: TickPoint[],
  stale = false
): TickFetchState => {
  const first = points[0]?.[0];
  const end = points.at(-1)?.[0];
  const gap = file === 'tail' && first !== undefined && (state.last === undefined || first > state.last + 1);
  const behind = file === 'tail' && (stale || end === undefined || (state.last !== undefined && end <= state.last));
  return {
    ...state,
    full: gap || behind || (file === 'tail' && state.full),
    last: end === undefined ? state.last : Math.max(end, state.last ?? end),
  };
};

// A permanent answer means there is no tail file: a 4xx other than 408/429, or a 200 that is not JSON (an SPA
// page) or is JSON of another shape (a misrouted path; parseTickFeed names it InvalidTickFeed).
const missingFile = (error: unknown) => {
  const status = httpStatus(error);
  return (
    error instanceof SyntaxError ||
    (error instanceof Error && error.name === 'InvalidTickFeed') ||
    (typeof status === 'number' && status >= 400 && status < 500 && status !== 408 && status !== 429)
  );
};

/** Any failure may leave a gap: read the full file next. A missing tail file: full file only from then on. */
export const afterTickFailure = (state: TickFetchState, file: TickFile, error: unknown): TickFetchState => ({
  ...state,
  full: true,
  noTail: state.noTail || (file === 'tail' && missingFile(error)),
});

/** The tab was hidden: the tail may no longer reach the last second read. */
export const resumeTicks = (state: TickFetchState): TickFetchState => ({ ...state, full: true });
